import { FC, useMemo } from "react";
import { Stack } from "@mui/material";
import { CommitmentsPageHeaderMetadataCard } from "../../../../common/components/metadata-card/CommitmentsPageHeaderMetadataCard";
import { useAppSelector } from "@/store/hooks";
import { azureSavingsPlansGranularRealCostSelector } from "@/store/commitments/selectors/azure/coverage-and-utilization/data/granular/totals/azureSavingsPlansGranularRealCostSelector";
import { azureSavingsPlansForGranularLoadingSelector } from "@/store/commitments/selectors/azure/coverage-and-utilization/data/loading/azureSavingsPlansForGranularLoadingSelector";
import { azureSavingsPlansGranularDataUncoveredUsageSelector } from "@/store/commitments/selectors/azure/coverage-and-utilization/data/granular/totals/azureSavingsPlansGranularDataUncoveredUsageSelector";
import { organisationQuarterStartMonthSelector } from "@/store/common/selectors/org-current-settings/organisationQuarterStartMonthSelector";
import { commitmentsDatesSelector } from "@/store/commitments/selectors/common/view-options/commitmentsDatesSelector";
import { generateFromToFromDates } from "@/components/pages/commtiments/utilization-and-coverage/utils/helpers/generateFromToFromDates";
import { money } from "@/utils/numeral/money";
import { azureSavingsPlansNetSavingsSelector } from "@/store/commitments/selectors/azure/coverage-and-utilization/data/totals/azureSavingsPlansNetSavingsSelector";
import { CommitmentsChartMetadataCard } from "@/components/pages/commtiments/common/components/chart/CommitmentsChartMetadataCard";
import { azureSavingsPlansGranularDataTotalCommitmentSelector } from "@/store/commitments/selectors/azure/coverage-and-utilization/data/granular/totals/azureSavingsPlansGranularDataTotalCommitmentSelector";
import { azureSavingsPlansGranularDataTotalCoverageSelector } from "@/store/commitments/selectors/azure/coverage-and-utilization/data/granular/totals/azureSavingsPlansGranularDataTotalCoverageSelector";
import { azureSavingsPlansGranularDataTotalUtilizationSelector } from "@/store/commitments/selectors/azure/coverage-and-utilization/data/granular/totals/azureSavingsPlansGranularDataTotalUtilizationSelector";
import { azureSavingsPlanDataIsNotAvailableSelector } from "@/store/commitments/selectors/azure/coverage-and-utilization/data/azureSavingsPlanDataIsNotAvailableSelector";

export const AzureUtilizationAndCoverageMetadataCosts: FC = () => {
  const netSavings = useAppSelector(azureSavingsPlansNetSavingsSelector);
  const realCost = useAppSelector(azureSavingsPlansGranularRealCostSelector);
  const loading = useAppSelector(azureSavingsPlansForGranularLoadingSelector);
  const uncoveredUsage = useAppSelector(
    azureSavingsPlansGranularDataUncoveredUsageSelector,
  );
  const totalCommitment = useAppSelector(
    azureSavingsPlansGranularDataTotalCommitmentSelector,
  );

  const totalCoverage = useAppSelector(
    azureSavingsPlansGranularDataTotalCoverageSelector,
  );
  const totalUtilization = useAppSelector(
    azureSavingsPlansGranularDataTotalUtilizationSelector,
  );

  const quarterStartMonth = useAppSelector(
    organisationQuarterStartMonthSelector,
  );

  const dates = useAppSelector(commitmentsDatesSelector);
  const datesFromTo = useMemo(() => {
    return generateFromToFromDates(dates, quarterStartMonth);
  }, [dates, quarterStartMonth]);

  const notAvailable = useAppSelector(
    azureSavingsPlanDataIsNotAvailableSelector,
  );
  const notAvailableMessage = notAvailable ? "N/A" : "-";
  const utilizationAmount =
    totalUtilization?.amount === null
      ? notAvailableMessage
      : money(totalUtilization?.amount);

  return (
    <Stack
      direction="row"
      border={1}
      borderRadius={2}
      borderColor="grey.300"
      minHeight={216}
    >
      <Stack borderRight={1} borderColor="grey.300" pl={1} flex={1}>
        <CommitmentsPageHeaderMetadataCard
          primary="Real Cost"
          loading={loading}
          value={money(realCost)}
          tooltip={`Total costs paid for all resources eligible for Savings Plans including Savings Plan Recurring Fees.`}
          sx={{
            height: "50%",
            justifyContent: "center",
          }}
        />
        <CommitmentsPageHeaderMetadataCard
          primary="Net Savings"
          loading={loading}
          value={money(netSavings)}
          tooltip={`Total net savings from commitments for the date range ${datesFromTo}. Calculated by comparing the usage cost covered by Savings Plans to the public On-Demand cost.`}
          sx={{
            height: "50%",
            justifyContent: "center",
          }}
        />
      </Stack>
      <Stack flex={1}>
        <CommitmentsPageHeaderMetadataCard
          primary="Not Covered (On-Demand)"
          loading={loading}
          value={money(uncoveredUsage)}
          tooltip={`Eligible On-Demand spend not covered by Savings Plans for date range ${datesFromTo}.`}
          sx={firstItemSx}
        />

        <CommitmentsChartMetadataCard
          loading={loading}
          title="Commitments"
          value={money(totalCommitment)}
          tooltip={`Total SP commitments ${datesFromTo}.`}
          sx={secondItemSx}
        />
      </Stack>{" "}
      <Stack flex={1}>
        <CommitmentsChartMetadataCard
          loading={loading}
          title="Covered"
          value={money(totalCoverage?.amount)}
          secondaryValue={
            totalCoverage?.percent ? `(${totalCoverage?.percent}%)` : ""
          }
          tooltip={`Total cost and percentage of resources covered by SPs ${datesFromTo}.`}
          sx={firstItemSx}
        />
        <CommitmentsChartMetadataCard
          title="Utilization"
          loading={loading}
          value={utilizationAmount}
          secondaryValue={
            totalUtilization?.percent ? `(${totalUtilization?.percent}%)` : ""
          }
          tooltip={`Total SPs utilization ${datesFromTo}.`}
          sx={secondItemSx}
        />
      </Stack>
    </Stack>
  );
};

const firstItemSx = {
  borderBottom: 1,
  borderColor: "grey.300",
  justifyContent: "center",
  pl: 2,
  pr: 1,
  height: "50%",
};

const secondItemSx = {
  justifyContent: "center",
  pl: 2,
  pr: 1,
  height: "50%",
};
